import React from 'react';
import { View, Text } from 'react-native';
import { Tooltip } from 'react-native-paper';

interface KpiItem {
  label: string;
  value: number;
  color: string;
  textColor?: string;
  unit?: string;
  subtitle?: string;
}

interface LogKpiRowProps {
  items: KpiItem[];
  children?: React.ReactNode;
}

function formatKpiValue(v: number): string {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(v);
}

// Pastel / light colours (schemeSet3, schemePaired light variants) are unreadable on white.
// If luminance > 0.65, fall back to dark grey for the value text; keep the colour on the border.
function safeTextColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#333333';
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.65 ? '#333333' : hex;
}

export default function LogKpiRow({ items, children }: LogKpiRowProps) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, padding: 10, justifyContent: 'center', alignItems: 'center' }}>
      {items.map((item, idx) => (
        <Tooltip key={`${item.label}-${idx}`} title={item.label}>
          <View
            testID={`kpi-card-${item.label}`}
            accessible={true}
            style={{
              minWidth: 110,
              maxWidth: 160,
              flex: 1,
              borderLeftWidth: 4,
              borderLeftColor: item.color,
              borderRadius: 8,
              padding: 12,
              backgroundColor: '#FFFFFF',
              elevation: 2,
              shadowColor: '#000',
              shadowOpacity: 0.08,
              shadowRadius: 4,
              shadowOffset: { width: 0, height: 2 },
            }}
          >
            <Text numberOfLines={1} ellipsizeMode="tail" style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
              {item.label}
            </Text>
            <Text style={{ fontSize: 22, fontWeight: 'bold', color: safeTextColor(item.color) }}>
              {formatKpiValue(item.value)}{item.unit ? <Text style={{ fontSize: 12, fontWeight: 'normal', color: '#888' }}> {item.unit}</Text> : null}
            </Text>
            {item.subtitle ? (
              <Text numberOfLines={1} ellipsizeMode="tail" style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                {item.subtitle}
              </Text>
            ) : null}
          </View>
        </Tooltip>
      ))}
      {children}
    </View>
  );
}
